import { monthlyTrendData } from "@/mocks/reportsData";

export default function MonthlyTrendChart({ onExport }: { onExport?: () => void }) {
  const w = 500;
  const h = 260;
  const padLeft = 45;
  const padRight = 20;
  const padTop = 15;
  const padBottom = 30;
  const chartW = w - padLeft - padRight;
  const chartH = h - padTop - padBottom;

  const maxRevenue = 80;
  const maxLeads = 200;

  const revPoints = monthlyTrendData.map((d, i) => ({
    x: padLeft + (i / (monthlyTrendData.length - 1)) * chartW,
    y: padTop + chartH - (d.revenue / maxRevenue) * chartH,
  }));

  const leadPoints = monthlyTrendData.map((d, i) => ({
    x: padLeft + (i / (monthlyTrendData.length - 1)) * chartW,
    y: padTop + chartH - (d.leads / maxLeads) * chartH,
  }));

  const revPath = revPoints.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
  const leadPath = leadPoints.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");

  const yTicksRev = [0, 20, 40, 60, 80];
  const yTicksLeads = [0, 50, 100, 150, 200];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Monthly Trend</h3>
        {onExport && (
          <button
            onClick={onExport}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors cursor-pointer"
            title="Export CSV"
          >
            <i className="ri-download-line text-sm" />
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full max-w-full" style={{ minWidth: "500px" }}>
          {/* Grid lines & Y axis labels */}
          {yTicksRev.map((val) => {
            const y = padTop + chartH - (val / maxRevenue) * chartH;
            return (
              <g key={`rev-${val}`}>
                <line x1={padLeft} y1={y} x2={w - padRight} y2={y} stroke="#f1f5f9" strokeWidth="1" />
                <text x={padLeft - 6} y={y + 4} textAnchor="end" className="text-[10px] fill-gray-400" fontFamily="inherit">₹{val}K</text>
              </g>
            );
          })}

          {/* Revenue line */}
          <path d={revPath} fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          {revPoints.map((p, i) => (
            <g key={`rev-dot-${i}`}>
              <circle cx={p.x} cy={p.y} r="4" fill="#fff" stroke="#4f46e5" strokeWidth="2" />
              <text x={p.x} y={p.y - 10} textAnchor="middle" className="text-[9px] font-semibold fill-indigo-600" fontFamily="inherit">
                ₹{monthlyTrendData[i].revenue}K
              </text>
            </g>
          ))}

          {/* Leads line */}
          <path d={leadPath} fill="none" stroke="#16a34a" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" strokeDasharray="6,3" />
          {leadPoints.map((p, i) => (
            <g key={`lead-dot-${i}`}>
              <circle cx={p.x} cy={p.y} r="3.5" fill="#fff" stroke="#16a34a" strokeWidth="2" />
            </g>
          ))}

          {/* X axis labels */}
          {monthlyTrendData.map((d, i) => (
            <text
              key={`x-${i}`}
              x={padLeft + (i / (monthlyTrendData.length - 1)) * chartW}
              y={h - 6}
              textAnchor="middle"
              className="text-[10px] fill-gray-500"
              fontFamily="inherit"
            >
              {d.month}
            </text>
          ))}
        </svg>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-2">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0.5 bg-indigo-600 rounded" />
          <span className="text-xs text-gray-500">Revenue</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0.5 bg-green-600 rounded" style={{ borderStyle: "dashed" }} />
          <span className="text-xs text-gray-500">Leads</span>
        </div>
      </div>
    </div>
  );
}